import React, { Component } from 'react';
import { getAuthHeaders, getEffectiveRole, SUPPORTED_LANGUAGES, urlMap, appNavigation } from '../../utils';
import { AppContext } from '../../context/AppContext';
import { loadContext as loadContextService } from '../../services/projects';
import { getWebLanguage, tWeb } from '../../i18n/sharedTranslations';
import ActionButton from '../components/ActionButton';
import ProjectTile from '../components/ProjectTile';
import { openSubscriptionTabInWallet } from '../../utils/subscriptionAccess';

const scaffoldLanguageLabel = (code) => {
  const language = SUPPORTED_LANGUAGES.find((item) => item.code === code);
  return language?.label || String(code || 'en').toUpperCase();
};

class ProjectsSection extends Component {
  static contextType = AppContext;

  t = (key, vars) => tWeb(getWebLanguage(), key, vars);

  state = {
    loading: true,
    creating: false,
    creatingDraft: false,
    creatingScaffold: false,
    error: null,
    projects: [],
    drafts: [],
    draftsError: null,
    scaffolds: [],
    scaffoldsError: null,
    activeProjectId: null,
    view: 'active', // 'active' | 'archived' | 'all'
    scaffoldView: 'active', // 'active' | 'archived' | 'all'
    publisherHomeTab: 'scaffolds', // 'scaffolds' | 'newsletter'
    counts: { active: null, archived: null, all: null },
  };

  componentDidMount() {
    this.loadProjects();
    this.loadCounts();
    this.loadDrafts();
    const effectiveRole = getEffectiveRole();
    if (effectiveRole === 'publisher' || effectiveRole === 'admin') {
      this.loadScaffolds();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.focusProjectId && this.props.focusProjectId !== prevProps.focusProjectId) {
      this.tryFocusProject(this.props.focusProjectId);
    }
    if (prevState.view !== this.state.view) {
      this.loadProjects();
    }
  }

  async loadProjects() {
    const { view } = this.state;
    try {
      this.setState({ loading: true });
      const res = await fetch(`${urlMap.listProjects}?view=${encodeURIComponent(view)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const projects = await res.json();
      this.setState({ projects, loading: false, error: null }, () => {
        if (this.props.focusProjectId) {
          this.tryFocusProject(this.props.focusProjectId);
        }
      });
    } catch (err) {
      this.setState({ error: err.message || 'Failed to load', loading: false, projects: [] });
    }
  }

  async loadCounts() {
    try {
      const base = urlMap.listProjects.replace(/\/+$/, '');
      const res = await fetch(`${base}/counts`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const counts = await res.json();
      this.setState({ counts });
    } catch (err) {
      console.warn('Failed to load project counts:', err);
    }
  }

  async loadDrafts() {
    try {
      const res = await fetch(urlMap.listDrafts, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const drafts = await res.json();
      this.setState({ drafts: Array.isArray(drafts) ? drafts : [], draftsError: null });
    } catch (err) {
      this.setState({ drafts: [], draftsError: err.message || 'Failed to load drafts' });
    }
  }

  async loadScaffolds() {
    try {
      const res = await fetch(urlMap.listAuthorStoryScaffolds(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const scaffolds = await res.json();
      this.setState({ scaffolds: Array.isArray(scaffolds) ? scaffolds : [], scaffoldsError: null });
    } catch (err) {
      this.setState({ scaffolds: [], scaffoldsError: err.message || 'Failed to load scaffolds' });
    }
  }

  handleOpenScaffold = (scaffold) => {
    if (!scaffold?.id) return;
    this.props.navigate(appNavigation.scaffoldEditor(scaffold.id));
  };

  handleCreateScaffold = async () => {
    if (this.state.creatingScaffold) return;
    this.setState({ creatingScaffold: true });
    try {
      const res = await fetch(urlMap.createStoryScaffold(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          title: 'Untitled scaffold',
          description: 'A new story scaffold.',
          status: 'draft',
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.error || `HTTP_${res.status}`);

      const scaffoldId = payload?.id;
      if (!scaffoldId) throw new Error('MALFORMED_DTO');

      await this.loadScaffolds();
      this.props.navigate(appNavigation.scaffoldEditor(scaffoldId));
    } catch (err) {
      console.error('Create scaffold failed:', err);
      if (String(err?.message || '').includes('SCAFFOLD_LIMIT_REQUIRES_PRO')) {
        openSubscriptionTabInWallet();
        alert('Upgrade to Pro to create more than one scaffold.');
        return;
      }
      alert('Could not create scaffold. Please try again.');
    } finally {
      this.setState({ creatingScaffold: false });
    }
  };

  tryFocusProject = (projectId) => {
    const p = this.state.projects.find((x) => x.id === projectId);
    if (p) this.handleSelect(p);
  };

  handleSelect = async (project) => {
    this.setState({ activeProjectId: project.id });
    try {
      const { project: full } = await loadContextService(project.id, this.context);
      this.props?.onSelect?.(full);
    } catch (e) {
      // Fallback to minimal data if fetch fails
      this.context?.setCurrentProject?.(project);
    }
    this.props.navigate(`/project/${project.id}`);
  };

  handleCreate = async () => {
    if (this.state.creating) return;
    this.setState({ creating: true });
    try {
      const res = await fetch(urlMap.projectEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ title: 'Untitled Project' }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP_${res.status}`);
      }
      const data = await res.json();
      const projectId = data?.id ?? data?.project?.id;
      if (!projectId || !/^[0-9a-fA-F-]{36}$/.test(projectId)) throw new Error('MALFORMED_DTO');
      try {
        await loadContextService(projectId, this.context);
      } catch {
        // Set minimal info if fetch fails
        this.context?.setCurrentProject?.({ id: projectId, title: 'Untitled Project' });
      }
      this.props.navigate(`/project/${projectId}/library`);
    } catch (err) {
      console.error('Create project failed:', err);
      alert('Could not create project. Please try again.');
    } finally {
      this.setState({ creating: false });
    }
  };

  setView = (view) => this.setState({ view });

  setScaffoldView = (scaffoldView) => this.setState({ scaffoldView });

  setPublisherHomeTab = (publisherHomeTab) => this.setState({ publisherHomeTab });

  handleContinueDraft = (draft) => {
    const draftId = draft?.id;
    if (!draftId) return;
    this.props.navigate(`/drafts/${draftId}/edit`);
  };

  handleCreateDraft = async () => {
    if (this.state.creatingDraft) return;
    this.setState({ creatingDraft: true });
    try {
      const res = await fetch(urlMap.createDraft, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          chapterTitle: 'Untitled Draft',
          chapterContent: 'Placeholder draft content. Start writing your story here.',
        }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP_${res.status}`);
      }
      const created = await res.json();
      if (created?.id) {
        this.props.navigate(`/drafts/${created.id}/edit`);
        return;
      }
      await this.loadDrafts();
    } catch (err) {
      console.error('Create draft failed:', err);
      alert('Could not create draft. Please try again.');
    } finally {
      this.setState({ creatingDraft: false });
    }
  };

  renderDraftsSection = () => {
    const t = this.t;
    const { drafts, draftsError, creatingDraft } = this.state;
    const recentDrafts = drafts
      .slice(0, 5)
      .map((draft) => ({
        id: draft.id,
        title: draft.chapter_title || 'Untitled Draft',
        updatedAt: draft.updated_at || draft.created_at || null,
      }));

    return (
      <section className="drafts-section" aria-label={t('home.recentDrafts')}>
        <div className="drafts-header">
          <h3 className="recent-drafts__title">{t('home.recentDrafts')}</h3>
          <ActionButton
            className="btn primary cta-draft"
            onClick={this.handleCreateDraft}
            ariaLabel={t('home.createNewDraft')}
            disabled={creatingDraft}
          >
            {creatingDraft ? t('home.creating') : t('home.newDraft')}
          </ActionButton>
        </div>

        {draftsError && <div className="projects-error">{t('home.couldNotLoadDrafts')}: {draftsError}</div>}

        {!draftsError && recentDrafts.length === 0 ? (
          <div className="projects-empty">{t('home.noDraftsYet')}</div>
        ) : (
          <div className="draft-tile-grid">
            {recentDrafts.map((draft) => (
              <button
                key={draft.id}
                type="button"
                className="draft-tile"
                onClick={() => this.handleContinueDraft(draft)}
                aria-label={`Open draft ${draft.title}`}
              >
                <span className="draft-tile__title">{draft.title}</span>
                <span className="draft-tile__meta">
                  {draft.updatedAt ? `Updated ${new Date(draft.updatedAt).toLocaleDateString()}` : 'Continue writing'}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
    );
  };

  handleSelectChapter = (project, chapter) => {
    if (!project?.id || !chapter?.id) return;
    this.setState({ activeProjectId: project.id });
    this.props.navigate(`/project/${project.id}/chapter/${chapter.id}`);
  };

  renderTile(project) {
    const isActive = this.state.activeProjectId === project.id;
    return (
      <ProjectTile
        key={project.id}
        project={project}
        isActive={isActive}
        onSelect={this.handleSelect}
        onSelectChapter={this.handleSelectChapter}
      />
    );
  }

  render() {
    const t = this.t;
    const { loading, error, projects, creating, view, counts, publisherHomeTab } = this.state;
    const effectiveRole = getEffectiveRole();
    const isPublisher = effectiveRole === 'publisher';
    const isPublisherOrAdmin = effectiveRole === 'publisher' || effectiveRole === 'admin';

    const fallbackActive = projects.filter((p) => !p.archived_at).length;
    const fallbackArchived = projects.filter((p) => !!p.archived_at).length;
    const activeCount = counts.active ?? fallbackActive;
    const archivedCount = counts.archived ?? fallbackArchived;
    const allCount = counts.all ?? projects.length;

    const visible = projects;

    const writingProjectsSection = (
      <section className="projects-section projects-section--balanced projects-section--after-scaffold">
          <div className="projects-header">
            <div className="projects-title-row">
              <h2 className="projects-title">{t('home.writingProjects')}</h2>
              <div className="projects-filters" role="tablist" aria-label={t('home.projectFilters')}>
                <ActionButton
                  className={`filter-tab ${view === 'active' ? 'active' : ''}`}
                  onClick={() => this.setView('active')}
                  ariaLabel={t('home.showActiveProjects')}
                  ariaPressed={view === 'active'}
                >
                  {t('home.filter.active')} ({Number(activeCount) || 0})
                </ActionButton>
                <ActionButton
                  className={`filter-tab ${view === 'archived' ? 'active' : ''}`}
                  onClick={() => this.setView('archived')}
                  ariaLabel={t('home.showArchivedProjects')}
                  ariaPressed={view === 'archived'}
                >
                  {t('home.filter.archived')} ({Number(archivedCount) || 0})
                </ActionButton>
                <ActionButton
                  className={`filter-tab ${view === 'all' ? 'active' : ''}`}
                  onClick={() => this.setView('all')}
                  ariaLabel={t('home.showAllProjects')}
                  ariaPressed={view === 'all'}
                >
                  {t('home.filter.all')} ({Number(allCount) || 0})
                </ActionButton>
              </div>
            </div>
            <div className="projects-actions">
              <ActionButton
                className="btn primary cta-project"
                onClick={this.handleCreate}
                disabled={creating}
                ariaLabel={t('home.createNewProject')}
              >
                {creating ? t('home.creating') : t('home.newProject')}
              </ActionButton>
            </div>
          </div>

          {loading && (
            <div className="projects-loading">
              <div className="skeleton-bubble" />
              <div className="skeleton-bubble" />
              <div className="skeleton-bubble" />
              <div className="skeleton-bubble" />
            </div>
          )}

          {error && <div className="projects-error">Could not load projects: {error}</div>}

          {!loading && !error && (
            <div className="projects-gallery">
              <div className="projects-strip" role="listbox" aria-label={t('home.projects')}>
                {visible.length === 0 ? (
                  <div className="projects-empty">
                    {view === 'archived'
                      ? t('home.noArchivedProjects')
                      : t('home.noProjects')}
                  </div>
                ) : (
                  visible.map((p) => this.renderTile(p))
                )}
              </div>
            </div>
          )}
      </section>
    );

    const draftsSection = this.renderDraftsSection();
    const scaffoldSection = this.renderScaffoldsSection({ hideTitle: isPublisher });

    const publisherTabsSection = isPublisher ? (
      <section className="projects-section projects-section--balanced projects-section--publisher-home" aria-label="Publisher sections">
        <div className="projects-header">
          <div className="projects-title-row">
            <div className="projects-filters" role="tablist" aria-label="Publisher section tabs">
              <ActionButton
                className={`filter-tab ${publisherHomeTab === 'scaffolds' ? 'active' : ''}`}
                onClick={() => this.setPublisherHomeTab('scaffolds')}
                ariaLabel="Show scaffolds tab"
                ariaPressed={publisherHomeTab === 'scaffolds'}
              >
                Narrative scaffolds
              </ActionButton>
              <ActionButton
                className={`filter-tab ${publisherHomeTab === 'newsletter' ? 'active' : ''}`}
                onClick={() => this.setPublisherHomeTab('newsletter')}
                ariaLabel="Show newsletter tab"
                ariaPressed={publisherHomeTab === 'newsletter'}
              >
                Newsletter Agent
              </ActionButton>
            </div>
          </div>
        </div>

        {publisherHomeTab === 'scaffolds' ? (
          <div>{scaffoldSection}</div>
        ) : (
          <div className="publisher-home-newsletter-embed">
            <iframe
              title="Newsletter Agent"
              src="/publisher/panel?embed=1#newsletter"
              className="publisher-home-newsletter-frame"
              style={{ width: '100%', minHeight: '78vh', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--card)' }}
            />
          </div>
        )}
      </section>
    ) : null;

    return (
      <>
        {!isPublisher ? writingProjectsSection : null}
        {isPublisher ? publisherTabsSection : (isPublisherOrAdmin ? scaffoldSection : draftsSection)}
        {!isPublisher && (isPublisherOrAdmin ? draftsSection : scaffoldSection)}
      </>
    );
  }

  renderScaffoldsSection = (options = {}) => {
    const t = this.t;
    const hideTitle = Boolean(options?.hideTitle);
    const { scaffolds, scaffoldsError, creatingScaffold, scaffoldView } = this.state;
    const effectiveRole = getEffectiveRole();
    const isPublisherOrAdmin = effectiveRole === 'publisher' || effectiveRole === 'admin';

    const scaffoldStatus = (scaffold) => {
      const status = String(scaffold?.status || '').toLowerCase();
      return (status === 'published' || status === 'active') ? 'active' : 'archived';
    };

    const activeScaffoldCount = scaffolds.filter((scaffold) => scaffoldStatus(scaffold) === 'active').length;
    const archivedScaffoldCount = scaffolds.filter((scaffold) => scaffoldStatus(scaffold) === 'archived').length;
    const allScaffoldCount = scaffolds.length;

    const visibleScaffolds = scaffolds.filter((scaffold) => {
      if (scaffoldView === 'active') return scaffoldStatus(scaffold) === 'active';
      if (scaffoldView === 'archived') return scaffoldStatus(scaffold) === 'archived';
      return true;
    });

    if (!isPublisherOrAdmin) {
      return (
        <section className="projects-section scaffold-section" aria-label={t('home.createStory')}>
          <div className="projects-header">
            <div className="projects-title-row">
              <h2 className="projects-title">{t('home.createStory')}</h2>
            </div>
            <div className="projects-actions">
              <ActionButton
                className="btn primary"
                onClick={() => this.props.navigate(appNavigation.shortStoryChat)}
                ariaLabel={t('home.pickScaffold')}
              >
                {t('home.pickScaffold')}
              </ActionButton>
            </div>
          </div>

          <div className="projects-empty">
            {t('home.createStoryHint')}
          </div>
        </section>
      );
    }

    return (
      <section className="projects-section scaffold-section projects-section--balanced" aria-label={t('home.storyScaffolds')}>
        <div className="projects-header">
          <div className="projects-title-row">
            {!hideTitle ? <h2 className="projects-title">{t('home.storyScaffolds')}</h2> : null}
            <div className="projects-filters" role="tablist" aria-label={t('home.projectFilters')}>
              <ActionButton
                className={`filter-tab ${scaffoldView === 'active' ? 'active' : ''}`}
                onClick={() => this.setScaffoldView('active')}
                ariaLabel={t('home.showActiveProjects')}
                ariaPressed={scaffoldView === 'active'}
              >
                {t('home.filter.active')} ({activeScaffoldCount})
              </ActionButton>
              <ActionButton
                className={`filter-tab ${scaffoldView === 'archived' ? 'active' : ''}`}
                onClick={() => this.setScaffoldView('archived')}
                ariaLabel={t('home.showArchivedProjects')}
                ariaPressed={scaffoldView === 'archived'}
              >
                {t('home.filter.archived')} ({archivedScaffoldCount})
              </ActionButton>
              <ActionButton
                className={`filter-tab ${scaffoldView === 'all' ? 'active' : ''}`}
                onClick={() => this.setScaffoldView('all')}
                ariaLabel={t('home.showAllProjects')}
                ariaPressed={scaffoldView === 'all'}
              >
                {t('home.filter.all')} ({allScaffoldCount})
              </ActionButton>
            </div>
          </div>
          <div className="projects-actions">
          <ActionButton
            className="btn primary cta-scaffold"
            onClick={this.handleCreateScaffold}
            ariaLabel={t('home.createNewScaffold')}
            disabled={creatingScaffold}
          >
            {creatingScaffold ? t('home.creating') : t('home.newScaffold')}
          </ActionButton>
          </div>
        </div>

        {scaffoldsError && <div className="projects-error">{t('home.couldNotLoadScaffolds')}: {scaffoldsError}</div>}

        {!scaffoldsError && visibleScaffolds.length === 0 ? (
          <div className="projects-empty">{t('home.noScaffoldsYet')}</div>
        ) : (
          <div className="scaffold-tile-grid">
            {visibleScaffolds.slice(0, 6).map((scaffold) => (
              <button
                key={scaffold.id}
                type="button"
                className="scaffold-tile"
                onClick={() => this.handleOpenScaffold(scaffold)}
                aria-label={t('home.openScaffold', { title: scaffold.title || t('home.untitledScaffold') })}
              >
                <span className="scaffold-tile__avatar" aria-hidden="true">
                  <span className="story-ring">
                    <span className="story-bubble">{(scaffold.title?.[0] || 'S').toUpperCase()}</span>
                  </span>
                </span>
                <span className="scaffold-tile__body">
                  <span className="scaffold-tile__title">{scaffold.title || t('home.untitledScaffold')}</span>
                  <span className="scaffold-tile__subtitle">{scaffold.description || t('home.storyScaffold')}</span>
                </span>
                <span className="scaffold-tile__meta">
                  <span className={`scaffold-tile__status ${(scaffold.status === 'published' || scaffold.status === 'active') ? 'published' : 'draft'}`}>
                    {(scaffold.status === 'published' || scaffold.status === 'active') ? t('home.published') : t('home.draft')}
                  </span>
                  <span className="scaffold-tile__language">{scaffoldLanguageLabel(scaffold.questionLanguage || scaffold.language || 'en')}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
    );
  };

}

export default ProjectsSection;
